import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { message, Spin } from 'antd';
import Header from '../../components/Header/header';
import Button from '../../components/Basic/button';
import FormModal from '../../components/Modal/FormModal';
import * as Actions from '../../store/slices/passwordPolicy/passwordPolicy';
import style from './PasswordPolicy.module.css';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';

interface Props {
  PasswordPolicy_Red: any;
  getPasswordPolicy: () => any;
  upsertPasswordPolicy: (data: any) => any;
  deletePasswordPolicy: () => any;
}

const PasswordPolicy: React.FC<Props> = ({
  PasswordPolicy_Red,
  getPasswordPolicy,
  upsertPasswordPolicy,
  deletePasswordPolicy,
}) => {
  const policy = PasswordPolicy_Red?.data;
  const loading = PasswordPolicy_Red?.loading;

  const [form, setForm] = useState({ length: 0, capitalLetters: 0, numbers: 0, specialCharacters: 0 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPasswordPolicy();
  }, [getPasswordPolicy]);

  useEffect(() => {
    if (policy) {
      setForm({
        length: policy.length ?? 0,
        capitalLetters: policy.capitalLetters ?? 0,
        numbers: policy.numbers ?? 0,
        specialCharacters: policy.specialCharacters ?? 0,
      });
    }
  }, [policy]);

  const handleChange = (field: string, value: number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      id: policy?.id ?? 0,
      ...form,
      createdAt: policy?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const result = await upsertPasswordPolicy(payload);
    setSaving(false);
    if (result?.success) {
      message.success('Password policy saved successfully');
      getPasswordPolicy();
    } else {
      message.error('Failed to save password policy');
    }
  };

  const handleDelete = async () => {
    const result = await deletePasswordPolicy();
    setDeleteOpen(false);
    if (result?.success) {
      message.success('Password policy deleted');
      getPasswordPolicy();
    } else {
      message.error('Failed to delete password policy');
    }
  };

  const fields = [
    { key: 'length', label: 'Minimum Length', description: 'Minimum number of characters required' },
    { key: 'capitalLetters', label: 'Capital Letters', description: 'Minimum number of uppercase letters required' },
    { key: 'numbers', label: 'Numbers', description: 'Minimum number of numeric digits required' },
    { key: 'specialCharacters', label: 'Special Characters', description: 'Minimum number of special characters required' },
  ];

  return (
    <>
      <Header />
      <FormModal
        modalTitle="Delete Password Policy"
        Subtitle="Are you sure you want to delete the password policy?"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
      <div className={style.Route}>
        <h5>Password Policy</h5>
        <div className={style.btns}>
          {policy && hasPermission(PERMISSIONS.PASSWORD_POLICY_DELETE) && (
            <Button Disable={false} Text="Delete Policy" buttonClass={style.deleteBtn} onClick={() => setDeleteOpen(true)} />
          )}
          {hasPermission(PERMISSIONS.PASSWORD_POLICY_UPSERT) && (
            <Button Text={saving ? 'Saving...' : 'Save Policy'} buttonClass={style.saveBtn} onClick={handleSave} Disable={saving} />
          )}
        </div>
      </div>

      <div className={style.container}>
        {loading ? (
          <div className={style.loader}><Spin /></div>
        ) : (
          <div className={style.grid}>
            {fields.map(({ key, label, description }) => (
              <div key={key} className={style.card}>
                <div className={style.cardHeader}>
                  <span className={style.cardLabel}>{label}</span>
                  <span className={style.cardDesc}>{description}</span>
                </div>
                <div className={style.counter}>
                  <button className={style.counterBtn} disabled={(form as any)[key] <= 0} onClick={() => handleChange(key, Math.max(0, (form as any)[key] - 1))}>−</button>
                  <input
                    type="number"
                    className={style.counterInput}
                    value={(form as any)[key]}
                    min={0}
                    onChange={(e) => handleChange(key, Math.max(0, parseInt(e.target.value) || 0))}
                  />
                  <button className={style.counterBtn} onClick={() => handleChange(key, (form as any)[key] + 1)}>+</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {policy && (
          <div className={style.meta}>
            <span>Last updated: {new Date(policy.updatedAt).toLocaleString()}</span>
          </div>
        )}
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return { PasswordPolicy_Red: state.PasswordPolicy_Red };
}

export default connect(mapStateToProps, Actions)(PasswordPolicy);
